import { useReducer, useState, useCallback, useEffect, useRef, useMemo } from 'react';

/**
 * 注意！derivedStateFromProp 可能要做 useMemo
 * @param derivedStateFromProp
 * @return {[state:*, setState:((value: *) => void)]} [state, setState]
 */
const useDerivedState = (derivedStateFromProp) => {
	const [state, setState] = useState(derivedStateFromProp);
	const [prevStateFromProp, setPrevStateFromProp] = useState(derivedStateFromProp);
	if (derivedStateFromProp !== prevStateFromProp) {
		setState(derivedStateFromProp);
		setPrevStateFromProp(derivedStateFromProp);
	}
	return [state, setState];
};

const useForceUpdate = () => {
	const [, forceUpdate] = useReducer(x => x + 1, 0);
	return forceUpdate;
};

/**
 * @param {boolean} initFlag
 * @return {[flag:boolean, setFlag:(function(): void), unsetFlag:(function(): void)]} [flag, setFlag, unsetFlag]
 */
const useBooleanFlag = (initFlag) => {
	const [flag, _setFlag] = useState(initFlag);
	const setFlag = useCallback(() => _setFlag(true), []);
	const unsetFlag = useCallback(() => _setFlag(false), []);
	return [flag, setFlag, unsetFlag];
};

/**
 * 注意！onFlag 應該要再用 useCallback 包裝
 * @param {*} initFlag
 * @return {[flag:*, setFlag:(function(*): void), unsetFlag:(function(): void)]} [flag, setFlag, unsetFlag]
 */
const useObjectFlag = (initFlag) => {
	const [flag, _setFlag] = useState(initFlag);
	const setFlag = useCallback(obj => _setFlag(obj), []);
	const unsetFlag = useCallback(() => _setFlag(false), []);
	return [flag, setFlag, unsetFlag];
};

/**
 * @typedef UseAjaxOptionsType
 * @property {'get' | 'post'} method
 * @property {number} activeIndex
 * @property {() => void} onRequest
 * @property {() => void} onComplete
 * @property {() => void} onSuccess
 * @property {() => void} onError
 * @property {HTMLElement} noticeContainer
 * @property {string} errCode
 * @property {{ [key: number]: string }} errMsg
 */

/**
 * @param {string} url
 * @param {UseAjaxOptionsType} options
 * @param {boolean} initLoading
 * @returns {[() => Promise<{ type: 'success'; data: any; } | { type: 'fail'; xhr: any; }>, boolean, () => void]}
 */
const useAjax = (url, options = {}, initLoading = false) => {
	const ajaxRef = useRef();
	const [_options, setOptions] = useState(options);
	const [loading, setLoading] = useState(initLoading);

	useEffect(() => {
		let isChange = false;
		if (JSON.stringify(options) !== JSON.stringify(_options)) {
			isChange = true;
		}
		// Object.keys(options).forEach(key => {
		// 	if (_options[key] !== options[key]) {
		// 		isChange = true;
		// 	}
		// });
		if (isChange) {
			setOptions(options);
		}
	}, [_options, options]);

	const ajax = useCallback((data = {}) => {
		return new Promise((res, rej) => {
			ajaxRef.current = new _Ajax({
				url,
				noticeContainer: document.body,
				onRequest: () => setLoading(true),
				onSuccess: (resData) => {
					setLoading(false);
					res({
						type: 'success',
						data: resData
					});
				},
				onError: (xhr) => {
					setLoading(false);
					res({
						type: 'fail',
						xhr: xhr
					});
				},
				..._options,
				data,
			}).send();
		});
	}, [url, _options]);

	const abort = useCallback(() => {
		if (ajaxRef.current) {
			ajaxRef.current.cancel();
		}
	}, []);

	return [ajax, loading, abort];
};

const _getParent = (ref) => {
	const wrapper = ref.current;
	if (!wrapper) return null;
	const parent = wrapper.getParent();
	return parent;
};

const useFullHeight = (wrapperRef, getContainer = _getParent) => {
	const [height, setHeight] = useState('0px');

	useEffect(() => {
		if (wrapperRef) {
			const wrapper = wrapperRef.current;
			const parent = getContainer(wrapperRef);
			if (!parent) return;
			const paddingBottom = parent.getComputedSize()['padding-bottom'];
			setHeight(`calc(100% - ${wrapper.getCoordinates(parent).top - paddingBottom}px)`);
		}
	}, [wrapperRef, getContainer]);

	return height;
};

const useOutsideClick = (ref, func) => {
	useEffect(() => {
		const onClick = (e) => {
			if (!ref.current?.contains(e.target)) {
				func();
			}
		};
		window.addEventListener('click', onClick);

		return () => {
			window.removeEventListener('click', onClick);
		};
	}, [ref, func]);
};

const useInput = (ref) => {

};

export {
	useForceUpdate, useDerivedState, useBooleanFlag, useObjectFlag, useAjax, useFullHeight,
	useOutsideClick
};
